import { BehaviorSubject, Observable, of, combineLatest } from 'rxjs';
import { Deal } from './deal.model';
import { DealService } from './deal.service';
import { Injectable } from '@angular/core';
import { ModelStore } from '../../../shared/stores/model.store';
import * as _ from 'lodash';
import { switchMap, catchError, map, take } from 'rxjs/operators';

interface BorrowerDealsMap {
    [borrowerId: number]: number[];
}

@Injectable()
export class DealStore extends ModelStore<Deal> {

    private borrowerDealIds$ = new BehaviorSubject<BorrowerDealsMap>({});
    private borrowerDealRequests: number[] = [];

    constructor(private dealService: DealService) {
        super(dealService);
    }

    forBorrower(borrowerId: number): Observable<(Deal|null)[]> {
        return this.dealIdsForBorrower(borrowerId).pipe(
            switchMap((dealIds) => {
                const dealObservables = _.map(dealIds, (dealId) => this.get(dealId));
                if (!dealObservables.length) {
                    return of([]);
                }
                return combineLatest([...dealObservables]);
            })
        );
    }

    protected dealIdsForBorrower(borrowerId: number): Observable<number[]> {
        return this.borrowerDealIds$.pipe(
            switchMap((borrowerDealIds: BorrowerDealsMap) => {
                if (this.borrowerDealRequests.includes(borrowerId)) {
                    return of(borrowerDealIds[borrowerId]);
                }
                this.borrowerDealRequests.push(borrowerId);
                return this.dealService.dealsForBorrower(borrowerId).pipe(
                    catchError((err) => {
                        this.borrowerDealRequests.splice(this.borrowerDealRequests.indexOf(borrowerId), 1);
                        return of([]);
                    }),
                    map((deals: Deal[]) => {
                        _.each(deals, (deal) => this.inject(deal));
                        return _.map(deals, (deal) => deal.id);
                    })
                );
            })
        );
    }

    protected afterInject(model) {
        this.borrowerDealIds$.pipe(take(1)).subscribe((borrowerDealIds: BorrowerDealsMap) => {
            const existingList = borrowerDealIds[model.borrowerId];
            if (!existingList) {
                borrowerDealIds[model.borrowerId] = [model.id];
                return this.borrowerDealIds$.next(borrowerDealIds);
            }
            if (!existingList.includes(model.id)) {
                existingList.push(model.id);
            }
            this.borrowerDealIds$.next(borrowerDealIds);
        });
    }
}
